import { Link } from 'react-router-dom';

import { avatarTone, initialsOf } from '../../../common/libs/initials';
import { IconGithub, IconTasks } from '../../../common/icons';
import AgentFace from '../../../common/components/agent-face/agent-face';
import { useAuthorNames } from '../../../global/contexts/lookups/lookups';

import '../styles/trail.css';

// La trayectoria de una tarea: quién la hizo, con qué, cuándo, y qué se decidió.
//
// EL DATO QUE HACE ESTA PANTALLA POSIBLE, y que no es obvio: ACEPTAR NO LIMPIA
// LA RESERVA. `ReviewManager` pasa `workflow_state` a `done` y no toca
// `claimed_by`, `claimed_by_token`, `claimed_session` ni las fechas — solo la
// DEVOLUCIÓN las limpia, porque la tarea vuelve a la cola. Así que una tarea
// hecha conserva quién la tomó y con qué credencial, y eso es exactamente
// «quién la hizo».
//
// Y por eso la misma fila se rotula distinto según el estado: mientras está
// `claimed` o `in_review` dice que alguien LA TIENE; una vez `done` dice que
// alguien LA HIZO. Presentar una reserva vencida de una tarea terminada como si
// estuviera en curso sería el mismo dato contando dos historias.

const DATE = new Intl.DateTimeFormat('es', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const DAY = new Intl.DateTimeFormat('es', { day: 'numeric', month: 'short', year: 'numeric' });

function when(raw, { full = true } = {}) {
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return (full ? DATE : DAY).format(date);
}

const ROUND_STATE = {
    open:     { label: 'Esperando decisión', tone: 'open' },
    approved: { label: 'Aprobada',           tone: 'approved' },
    returned: { label: 'Devuelta',           tone: 'returned' },
};

export default function TaskTrail({ detail }) {
    const { userName, tokenName, tokenKind } = useAuthorNames();

    const task = detail?.task ?? null;
    const reservation = detail?.reservation ?? null;
    const rounds = detail?.review_rounds ?? [];
    const commits = detail?.commits ?? [];
    const reviews = detail?.reviews ?? [];

    // Done means the reservation is a record, not a claim.
    const finished = task?.state === 'done';
    const working = task?.state === 'claimed' || task?.state === 'in_review';

    // Whether the work was delivered by an agent or by a person, read off the
    // token: a token belongs to somebody, so the person is always there and the
    // token is what says «through which agent».
    const agentKind = reservation?.token_id != null ? tokenKind(reservation.token_id) : null;

    // El nombre sale del paquete cuando viene (`by_name`); si no, del catálogo.
    // Una sola resolución para el texto Y para las iniciales del avatar: dos
    // caminos distintos dan un avatar que no corresponde al nombre de al lado.
    const quien = reservation
        ? (reservation.by_name ?? userName(reservation.by) ?? `Usuario #${reservation.by}`)
        : null;

    return (
        <section className="trail">
            <h3 className="trail__title">Trayectoria</h3>

            <ol className="trail__line">
                <Step tone="neutral" icon={<IconTasks />}>
                    <span className="trail__what">Creada</span>
                    <span className="trail__when">{when(task?.created_at) ?? '—'}</span>
                    {task?.origin_task_id && (
                        <span className="trail__note">
                            Salió de la{' '}
                            <Link to={`/admin/tasks/${task.origin_task_id}`}>
                                tarea #{task.origin_task_id}
                            </Link>
                        </span>
                    )}
                </Step>

                {/* QUIÉN LA HIZO. Persona y agente son dos cosas: el token
                    pertenece a alguien, así que la persona siempre está y el
                    token dice «a través de qué». */}
                {reservation && (
                    <Step tone={finished ? 'approved' : 'open'} avatar={quien}>
                        <span className="trail__what">
                            {finished ? 'La hizo' : working ? 'La tiene' : 'La tomó'}
                            {' '}
                            <strong>{quien}</strong>
                        </span>

                        {reservation.token_id != null ? (
                            <span className="trail__agent">
                                <AgentFace kind={agentKind} size={28} title={tokenName(reservation.token_id)} />
                                <span>
                                    por <strong>{tokenName(reservation.token_id)}</strong>
                                    {agentKind && <span className="trail__agent-kind"> · {agentKind}</span>}
                                </span>
                            </span>
                        ) : (
                            <span className="trail__note">
                                Desde el panel, sin agente: la credencial fue su propia sesión.
                            </span>
                        )}

                        <span className="trail__when">
                            {reservation.since ? `Tomada el ${when(reservation.since)}` : null}
                            {/* Una reserva vencida de una tarea terminada no es
                                un problema: dejó de importar cuando se aceptó. */}
                            {!finished && reservation.until && (
                                <> · vence el {when(reservation.until)}</>
                            )}
                        </span>
                    </Step>
                )}

                {/* LAS RONDAS (T04). Cada entrega abre la suya, con la VERSIÓN
                    EXACTA que se evalúa. Una ronda sin versión se dice como tal
                    — es una ausencia, no un valor — y no se puede aprobar. */}
                {rounds.map(round => {
                    const state = ROUND_STATE[round.state] ?? { label: round.state, tone: 'neutral' };
                    return (
                        <Step key={round.id} tone={state.tone}>
                            <span className="trail__what">
                                Ronda {round.number} · <strong>{state.label}</strong>
                                {round.self_approved && (
                                    <span
                                        className="trail__flag"
                                        title="Quien decidió es quien entregó. Queda registrado y no se presenta como revisión independiente."
                                    >
                                        autoaprobada
                                    </span>
                                )}
                            </span>

                            <span className="trail__when">
                                Entregada el {when(round.opened_at) ?? '—'}
                                {round.delivered_by != null && <> por <strong>{userName(round.delivered_by)}</strong></>}
                            </span>

                            {round.decided_at && (
                                <span className="trail__when">
                                    Decidida el {when(round.decided_at)}
                                    {round.decided_by != null && <> por <strong>{userName(round.decided_by)}</strong></>}
                                </span>
                            )}

                            {round.versioned ? (
                                <span className="trail__version">
                                    <Hash value={round.base_commit} label="base" />
                                    <span className="trail__arrow" aria-hidden="true">→</span>
                                    <Hash value={round.delivered_commit} label="entregado" />
                                </span>
                            ) : (
                                <span className="trail__note trail__note--warn">
                                    Sin versión registrada. Una ronda así <strong>no se puede
                                    aprobar</strong>: no hay contra qué medirla.
                                </span>
                            )}
                        </Step>
                    );
                })}

                {/* Las revisiones del modelo anterior al de rondas. Se muestran
                    cuando existen, porque una tarea vieja solo tiene esto. */}
                {rounds.length === 0 && reviews.map(review => (
                    <Step
                        key={review.id}
                        tone={review.result === 'accepted' ? 'approved' : 'returned'}
                    >
                        <span className="trail__what">
                            <strong>{review.result === 'accepted' ? 'Aceptada' : 'Devuelta'}</strong>
                            {review.reviewer_name && <> por {review.reviewer_name}</>}
                            {/* Con token es un agente; sin token, una persona
                                desde el panel. La diferencia importa al leer un
                                historial seis meses después. */}
                            <span className="trail__flag">
                                {review.by_agent ? 'agente' : 'persona'}
                            </span>
                        </span>
                        <span className="trail__when">{when(review.reviewed_at) ?? '—'}</span>
                        {review.reason && <span className="trail__reason">{review.reason}</span>}
                    </Step>
                ))}
            </ol>

            {/* LOS COMMITS. Un commit vinculado prueba el registro — que este
                código quedó atado a esta tarea — y nada más: ni cumplimiento,
                ni aceptación, ni integración, ni despliegue. Lo dice el propio
                backend en `reading` y conviene repetirlo donde se ven. */}
            <div className="trail__commits">
                <h4 className="trail__subtitle">
                    Commits
                    {commits.length > 0 && <span className="trail__count"> · {commits.length}</span>}
                </h4>

                {commits.length === 0 ? (
                    <p className="trail__note">Todavía no hay commits registrados para esta tarea.</p>
                ) : (
                    <ul className="trail__commit-list">
                        {commits.map(commit => (
                            <li key={commit.hash} className="trail__commit">
                                <Link
                                    to={`/admin/tasks/trace?hash=${commit.hash}`}
                                    className="trail__hash"
                                    title={`Ver la traza de ${commit.hash}`}
                                >
                                    <IconGithub />
                                    {String(commit.hash).slice(0, 10)}
                                </Link>

                                <span className="trail__commit-body">
                                    {commit.message && (
                                        <span className="trail__commit-message">{commit.message}</span>
                                    )}
                                    <span className="trail__commit-meta">
                                        {commit.author && <>{commit.author} · </>}
                                        {when(commit.committed_at, { full: false }) ?? 'sin fecha'}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function Step({ tone = 'neutral', icon, avatar, children }) {
    return (
        <li className={`trail__step trail__step--${tone}`}>
            <span className="trail__marker" aria-hidden="true">
                {avatar
                    ? (
                        <span className={`avatar trail__avatar avatar--tone-${avatarTone(avatar)}`}>
                            {initialsOf(avatar)}
                        </span>
                    )
                    : <span className="trail__dot">{icon}</span>}
            </span>
            <span className="trail__body">{children}</span>
        </li>
    );
}

// Un hash es para copiar y para seguir, así que va corto, en monoespaciada, con
// el valor entero en el title y enlazado a su traza.
function Hash({ value, label }) {
    if (!value) return null;
    return (
        <Link
            to={`/admin/tasks/trace?hash=${value}`}
            className="trail__hash trail__hash--small"
            title={`${label}: ${value}`}
        >
            {String(value).slice(0, 8)}
        </Link>
    );
}
